import type { ApiReferenceTranslations } from '@scalar/types/api-reference'

export const de = {
  common: {
    streamItem: 'Stream-Element',
    description: 'Beschreibung',
    httpMethod: 'HTTP-Methode',
    path: 'Pfad',
    copyDefault: 'Standardwert kopieren',
    copyExample: 'Beispielwert kopieren',
  },
  search: {
    label: 'Suchen',
    inputLabel: 'Suchbegriff eingeben',
    open: 'Suche öffnen',
    placeholder: 'Suchen...',
    clear: 'Suche löschen',
    noResults: 'Keine Ergebnisse gefunden',
    keyboardShortcut: 'Tastenkürzel:',
    command: 'Befehl',
    control: 'Steuerung',
    results: 'Suchergebnisse der Referenz',
    navigate: 'Navigieren',
    select: 'Auswählen',
    instructions:
      'Drücken Sie Pfeil nach oben oder unten zum Navigieren, Enter zum Auswählen und tippen Sie zum Filtern der Ergebnisse',
    entryHeading: 'Überschrift',
    entryOperation: 'Operation',
    entryTag: 'Tag',
    entryTagGroup: 'Tag-Gruppe',
    entryWebhook: 'Webhook',
  },
  navigation: {
    introduction: 'Einführung',
    closeGroup: 'Gruppe schließen',
    closeMenu: 'Menü schließen',
    openGroup: 'Gruppe öffnen',
    openMenu: 'Menü öffnen',
    operations: 'Operationen',
    endpoints: '{name}-Endpunkte',
    showAllEndpoints: 'Alle {name}-Endpunkte anzeigen',
    sidebarFor: 'Seitenleiste für {name}',
    mainContent: 'API-Dokumentation für {name}',
    collapsed: 'Eingeklappt',
    webhooks: 'Webhooks',
    channels: 'Kanäle',
  },
  server: {
    label: 'Server',
    select: 'Server auswählen',
  },
  info: {
    termsOfService: 'Nutzungsbedingungen',
  },
  asyncapi: {
    servers: 'Server',
    protocols: 'Protokolle',
  },
  clientLibraries: {
    heading: 'Client-Bibliotheken',
    more: 'Mehr',
    selectAll: 'Aus allen Clients auswählen',
    searchLabel: 'Clients suchen',
    changeClient: 'Sprache des Codebeispiels ändern',
  },
  operation: {
    codeSampleUnavailable: 'Für dieses Beispiel ist kein Codebeispiel verfügbar.',
    body: 'Body',
    cookies: 'Cookies',
    headers: 'Header',
    pathParameters: 'Pfadparameter',
    queryParameters: 'Query-Parameter',
    requestBody: 'Request Body',
    responses: 'Antworten',
    testRequest: 'Request testen',
    webhook: 'Webhook',
    selectedContentType: 'Ausgewählter Inhaltstyp',
    callbacks: 'Callbacks',
  },
  response: {
    xmlGenerationLimit:
      'Das XML-Beispiel überschreitet das Generierungslimit. Geben Sie ein serialisiertes XML-Beispiel an, um den vollständigen Inhalt anzuzeigen.',
    xmlGenerationFailed: 'XML-Beispiel konnte nicht generiert werden: {message}',
    exampleResponses: 'Beispielantworten',
    noBody: 'Kein Body',
    showSchema: 'Schema anzeigen',
    status: 'Status',
  },
  schema: {
    noAllowedValues: 'Keine Werte erfüllen diese Enum-Bedingung.',
    example: 'Beispiel',
    examples: 'Beispiele',
    default: 'Standard',
    schema: 'Schema',
    emptyObject: 'Leeres Objekt',
    showAdditionalProperties: 'Zusätzliche Eigenschaften anzeigen',
    forName: 'für {name}',
    showSchemaDetails: 'Schema-Details anzeigen',
    oneOf: 'Eines von',
    anyOf: 'Beliebiges von',
    allOf: 'Alle von',
    not: 'Nicht',
    unknownType: 'unbekannter Typ',
    propertyCount: 'Eigenschaften: {count}',
    headerCount: 'Header: {count}',
    recursiveReference: 'Rekursiver Verweis auf {name}',
    recursive: 'rekursiv',
    additionalProperties: 'zusätzliche Eigenschaften',
    const: 'Konstante',
    deprecated: 'veraltet',
    discriminator: 'Diskriminator',
    enum: 'enum',
    format: 'Format',
    greaterThan: 'größer als',
    keys: 'Schlüssel',
    lessThan: 'kleiner als',
    max: 'max.',
    min: 'min.',
    maxLength: 'max. Länge',
    minLength: 'min. Länge',
    multipleOf: 'Vielfaches von',
    nullable: 'nullable',
    propertyNames: 'Eigenschaftsnamen',
    pattern: 'Muster',
    copyPattern: 'Muster kopieren',
    readOnly: 'nur lesbar',
    required: 'erforderlich',
    hideValues: 'Werte ausblenden',
    showAllValues: 'Alle Werte anzeigen',
    type: 'Typ',
    unique: 'eindeutig',
    values: 'Werte',
    writeOnly: 'nur schreibbar',
  },
  download: {
    openapi: 'OpenAPI-Dokument herunterladen',
    asyncapi: 'AsyncAPI-Dokument herunterladen',
    asFormat: '{label} als {format}',
  },
  models: {
    label: 'Modelle',
  },
  actions: {
    copyAsMarkdown: 'Als Markdown kopieren',
    copied: 'Kopiert',
    copyMarkdownFailed: 'Markdown konnte nicht kopiert werden',

    copyLink: 'Link kopieren',
    copyLinkTo: 'Link zu {name} kopieren',
    copyToClipboard: 'Link in die Zwischenablage kopieren',
    copyEndpointUrl: 'Endpoint-URL kopieren',
    showMore: 'Mehr anzeigen',
  },
  agent: {
    askAi: 'KI fragen',
    askAiAgent: 'KI-Agent fragen',
    close: 'Client schließen',
  },
  mcp: {
    generate: 'MCP generieren',
    connect: 'MCP verbinden',
  },
  developerTools: {
    title: 'Entwicklertools',
    configure: 'Konfigurieren',
    share: 'Teilen',
    deploy: 'Bereitstellen',
    scalarConfiguration: 'Scalar-Konfiguration',
    theme: 'Theme',
    layout: 'Layout',
    layoutOptions: 'Layout-Optionen',
    intro:
      'Mit den Entwicklertools kannst du das Aussehen und Verhalten deiner Dokumentation anpassen. Du kannst deine Dokumentation auch über Scalar Registry teilen.',
    disableToolbarBefore: 'Um die Toolbar zu deaktivieren, setze',
    disableToolbarAfter: 'in deiner Konfiguration.',
    localhostOnly: 'Die Entwicklertools werden nur angezeigt, wenn sie auf localhost ausgeführt werden.',
    layoutModern: 'Modern',
    layoutClassic: 'Klassisch',
    showSidebar: 'Seitenleiste anzeigen',
    defaultOpenFirstTag: 'Ersten Tag standardmäßig öffnen',
    defaultOpenAllTags: 'Alle Tags standardmäßig öffnen',
    expandAll: 'Alle {label} erweitern',
    expandAllResponses: 'Alle Antworten erweitern',
    hideClientButton: 'Client-Schaltfläche ausblenden',
    hideDarkModeToggle: 'Dark-Mode-Umschalter ausblenden',
    hideModels: '{label} ausblenden',
    hideSearch: 'Suche ausblenden',
    showOperationId: 'Operation-ID anzeigen',
    hideTestRequestButton: 'Test-Request-Schaltfläche ausblenden',
    scalarDocs: 'Scalar Docs',
    deployDescription:
      'Veröffentliche deine Dokumentation auf Scalar, der modernen Dokumentationsplattform für deine API und alles Weitere.',
    shareTitle: 'API Reference teilen',
    shareDescription:
      'Lade dein OpenAPI-Dokument hoch, um deine API Reference mit anderen zu teilen. So einfach wie ein Klick.',
    uploadDocument: 'Dokument hochladen',
    temporaryLinkExpiration: 'Dein Dokument wird nach 7 Tagen automatisch gelöscht.',
    deployOnScalar: 'Auf Scalar veröffentlichen',
    deployFree: 'Veröffentliche deine Dokumentation kostenlos.',
    additionalFeaturesMightRequire: 'Zusätzliche Funktionen erfordern möglicherweise',
    generate: 'Generieren',
    passwordProtection: 'Passwortschutz',
    customDomains: 'Eigene Domains',
    freeFormContent: 'Freiform-Inhalte',
    cdnInfrastructure: 'CDN-Infrastruktur',
    pullFromGitHub: 'Von GitHub laden',
    markdownMdx: 'Markdown/MDX',
    spectralLinting: 'Spectral-Linting',
    jsonSchemaHosting: 'JSON-Schema-Hosting',
    askAi: 'KI fragen',
    mcpServers: 'MCP-Server',
    unableToExportDocument: 'Aktives Dokument konnte nicht exportiert werden',
    unknownError: 'Ein unbekannter Fehler ist aufgetreten',
  },
  gettingStarted: {
    swaggerEditor: 'Swagger Editor',
    description:
      'Willkommen bei Scalar API References + Swagger Editor, einem kostenlosen Open-Source-Tool, das deine Swagger/OAS-Datei in schöne API References verwandelt.',
    showExample: 'Beispiel anzeigen',
    uploadFile: 'Datei hochladen',
    integrations: 'INTEGRATIONEN',
    theming: 'THEMES',
    features: 'Funktionen',
    customize: 'Anpassen',
    customizeDescription: 'Nutze deine Typografie und Farbpaletten oder unsere Themes!',
    testing: 'Testen',
    testingDescription: 'Ein tief integrierter REST API Client (ebenfalls kostenlos und Open Source)',
    search: 'Suche',
    searchDescription: 'Vollständig integrierte Suche (mit fuse.js)',
    hosting: 'Hosting',
    hostingDescription: 'Kostenloses Subdomain-Hosting auf https://apidocumentation.com',
    openApiSwagger: 'OpenAPI und Swagger',
    openApiSwaggerDescription: 'Unterstützung für OpenAPI 3.1, OpenAPI 3.0 und Swagger 2.0',
    codeSamples: 'Codebeispiele',
    codeSamplesDescription: 'Codebeispiele, um deine API in den beliebtesten Sprachen zu präsentieren',
  },
  footer: {
    poweredByScalar: 'Bereitgestellt von Scalar',
  },
  authentication: {
    detailsRequired: 'Authentifizierung erforderlich',
    detailsOptional: 'Authentifizierung optional',
    apiKey: 'API-Schlüssel',
    mutualTLS: 'Gegenseitiges TLS',
    apiKeyCookie: 'Sende den API-Schlüssel im Cookie „{name}“.',
    apiKeyHeader: 'Sende den API-Schlüssel im Header „{name}“.',
    apiKeyQuery: 'Sende den API-Schlüssel im Abfrageparameter „{name}“.',
    title: 'Authentifizierung',
    accepts: 'Akzeptiert',
    allOf: 'alle von:',
    authentication: 'Authentifizierung',
    optional: 'Authentifizierung optional',
    oneOf: 'eines von:',
    required: 'Authentifizierung erforderlich',
    requires: 'Erfordert',
    scopes: 'OAuth-Scopes',
  },
} satisfies ApiReferenceTranslations
